<template>
	<div
		v-if="setup.discussionService"
		id="discussions"
		class="shared-discussions">
		<div
			v-if="setup.discussionService.openedDiscussionIds.length || setup.displayDiscussionList"
			:ref="setup._discussionsList"
			class="shared-discussions__opened"
			:class="{ 'shared-discussions__opened--list-closed': !setup.displayDiscussionList }">
			<o-card
				v-if="setup.displayDiscussionList"
				class="discussions-list-card">
				<div class="flex ai-c jc-sb mt-sm ph-sm">
					<h4 class="ma-0">{{ $t('common.shared.discussions.title') }}</h4>
					<span
						class="shared-discussions__list-close"
						@click="setup.hide()"/>
				</div>

				<o-chat-discussion-list
					:chat="setup.discussionService.chat"
					@select-discussion="setup.discussionService.open($event)">
					<template #append-discussion-list-header>
						<o-toggle
							v-model="setup.discussionService.unreadOnly"
							:label="$t('common.shared.discussions.unreadOnly')"
							reverse/>
					</template>

					<template #append-discussion-item="{ discussion }">
						<div class="flex g-xs mt-xs">
							<o-label
								v-if="setup.self.isAgency() || setup.self.isClient()"
								outline
								size="sm"
								color="brand">
								{{
									setup.self.isAgency()
										? setup.discussionService.getDiscussionAgencyCompanyName(discussion.id)
										: setup.discussionService.getDiscussionClientCompanyName(discussion.id)
								}}
							</o-label>
							<o-label
								v-for="(interlocutor, index) in discussion.interlocutors"
								:key="index"
								outline
								size="sm">
								{{ interlocutor.name }}
							</o-label>
						</div>
					</template>
				</o-chat-discussion-list>

				<o-loader
					:visible="setup.discussionService.showLoader"
					:message="$t('common.shared.discussions.loading')"/>
			</o-card>

			<o-card
				v-for="discussion in setup.discussionService.openedDiscussions"
				:key="discussion.id"
				class="discussion-card"
				:class="[
					setup.discussionService.minimizedDiscussions.includes(discussion.id) ? 'discussion-card--minimized' : 'discussion-card--opened',
					{ 'discussion-card--unread': discussion.unreadMessagesCount },
				]">
				<div
					v-if="setup.discussionService.minimizedDiscussions.includes(discussion.id)"
					class="discussion-card__expander">
					<o-avatar
						contain
						v-bind="discussion.interlocutors.first()"/>
					<span
						class="discussion-card__interlocutor orion-chat__interlocutors orion-chat__title"
						:data-unread-count="discussion.unreadMessagesCount">
						{{ discussion.title }}
					</span>
					<div class="discussion-card__header-actions">
						<o-icon
							v-if="setup.discussionService.getContextLinkFromDiscussionId(discussion.id)"
							v-tooltip="setup.discussionService.getContextLinkFromDiscussionId(discussion.id)?.label"
							ripple="default"
							font-icon="icon-interface-content-file"
							@click="setup.discussionService.goToContextAsync(discussion.id)"/>
						<o-icon
							ripple="default"
							font-icon="icon-interface-arrows-button-up"
							@click="setup.discussionService.expand(discussion.id)"/>
						<span
							class="discussion-card__close"
							@click="setup.discussionService.close(discussion.id)"/>
					</div>
				</div>

				<o-chat
					v-else
					:ref="el => { if (!!el) setup._discussions.value.push(el as unknown as OrionChat) }"
					hide-search
					focus-on-open
					:chat="setup.discussionService.chat"
					:discussion-id="discussion.id">
					<template #append-discussion-actions>
						<div class="discussion-card__header-actions">
							<o-icon
								v-if="setup.discussionService.getContextLinkFromDiscussionId(discussion.id)"
								v-tooltip="setup.discussionService.getContextLinkFromDiscussionId(discussion.id)?.label"
								ripple="default"
								font-icon="icon-interface-content-file"
								@click="setup.discussionService.goToContextAsync(discussion.id)"/>
							<o-icon
								ripple="default"
								font-icon="icon-interface-arrows-button-down"
								@click="setup.discussionService.minimize(discussion.id)"/>
							<span
								class="discussion-card__close"
								@click="setup.discussionService.close(discussion.id)"/>
						</div>
					</template>
				</o-chat>
			</o-card>
		</div>

		<div
			v-if="setup.displayDiscussionWrapper"
			class="shared-discussions__panel-trigger-wrapper">
			<o-button
				class="shared-discussions__panel-trigger"
				prefix-font-icon="icon-mail-chat-two-bubbles-square"
				color="brand"
				size="xl"
				@click="setup.show()"/>
			<span
				v-if="setup.discussionService.unreadMessagesCount"
				class="shared-discussions__panel-trigger-unread-count">
				{{ setup.discussionService.unreadMessagesCount }}
			</span>
		</div>
	</div>
</template>

<script setup lang="ts">
// #region Setup
import type { SharedDiscussionsEmits, SharedDiscussionsProps } from './SharedDiscussionsSetup';
import SharedDiscussionsSetup from './SharedDiscussionsSetup';
const emits = defineEmits<SharedDiscussionsEmits>() as SharedDiscussionsEmits;
const props = withDefaults(defineProps<SharedDiscussionsProps>(), SharedDiscussionsSetup.defaultProps);
const setup = new SharedDiscussionsSetup(props, emits);
// #endregion
</script>

<style scoped lang="less">
@import "@/styles/mixins.less";
@import "@/styles/variables.less";

.shared-discussions {
	will-change: right;
	position: fixed;
	z-index: 2;
	right: 1.5rem;
	bottom: 1rem;
	display: flex;
	gap: var(--space-sm);
	align-items: flex-end;
	transition: right 0.3s ease-out, bottom 0.3s ease-out;

	body.orion-body--footer-fixed-visible & {
		bottom: 7rem;
	}

	#app > #right-aside:not(:empty) ~ & {
		right: 20.5rem;
	}

	&__opened {
		display: flex;
		flex-direction: row-reverse;
		gap: 1rem;
		align-items: flex-end;

		@media @phone {
			margin-bottom: 3rem;
		}
	}

	&__list-close {
		.close(1.5rem);
	}

	&__panel-trigger {
		&-wrapper {
			position: relative;
			margin-bottom: 0.5rem;

			body.orion-body--footer-fixed-visible & {
				@media @phone {
					display: none;
				}
			}

			@media @phone {
				margin-right: -0.5rem;
				margin-bottom: 3.5rem;
			}
		}

		&-unread-count {
			.circle(1.25rem);
			.flex-align();

			position: absolute;
			top: -0.25rem;
			right: -0.25rem;
			font-weight: 600;
			line-height: 1.25rem;
			color: white;
			background: var(--danger);
		}
	}

	.discussions-list-card,
	.discussion-card--opened {
		overflow: hidden;
		width: 20rem;
		height: 30rem;

		:deep(.orion-card__body) {
			overflow: auto;
			padding: 0;
		}
	}

	.discussion-card--minimized {
		overflow: hidden;
		width: 20rem;
		height: 4rem;

		:deep(.orion-card__body) {
			padding: 0;
		}
	}

	.discussions-list-card {
		:deep(.orion-chat-discussion-list) {
			height: auto;
		}
	}

	.discussion-card {
		&__close {
			.close(1.5rem);

			margin-left: -0.5rem;
		}

		&__header-actions {
			display: flex;
			gap: var(--space-sm);
			align-items: center;
			margin-right: 0.25rem;

			:deep(.orion-icon) {
				font-size: 0.85rem;
			}
		}

		&__expander {
			display: flex;
			align-items: center;
			height: 4rem;
			padding: var(--fluid-10px);
			border-bottom: 0.0625rem solid white;

			.orion-avatar {
				flex: none;
				flex-basis: 2.8125rem;
				flex-shrink: 0;
				width: 2.8125rem;
				height: 2.8125rem;
				border: 0.125rem solid white;
				border-radius: 100%;
			}
		}

		&__interlocutor {
			margin-right: auto;
			margin-left: 0.5rem;
		}
	}

	@media @layoutNavBottom {
		bottom: 5.5rem;
	}

	@media @phone {
		bottom: 1rem;
	}
}

// Widget Zendesk (Agence) : ses éléments sont des iframes injectées par le script, positionnées
// en style inline, d'où les !important. Seul le lanceur a un id ; le message d'accueil
// (« Besoin d'aide ? ») est le conteneur qui le précède, la fenêtre de chat l'iframe du
// conteneur voisin.
// Le lanceur se place à gauche de la bulle comme le bouton Assistant (SharedBotpress), réduit à
// sa taille (48px), centré sur elle, sous les notifications (même z-index). Le message et la fenêtre le suivent,
// 0.5rem au-dessus, comme le fait Zendesk.
// Masqué avec la barre d'actions visible et la navigation en bas (comme le bouton Assistant), et
// sur @phone quand le panneau latéral droit est ouvert (en surimpression, il sortirait de l'écran).
:global(body.zendesk-loaded) {
	--zendesk-right: 5.5rem;
	--zendesk-bottom: 1.5rem;
}

:global(body.zendesk-loaded.orion-body--footer-fixed-visible) {
	--zendesk-bottom: 7.5rem;
}

:global(body.zendesk-loaded:has(#app > #right-aside:not(:empty))) {
	--zendesk-right: 24.5rem;
}

// z-index 1 : sous la bulle et son panneau de discussion (2), au-dessus du contenu (#arm-main, 1,
// placé avant dans le DOM)
// Même transition que la bulle : le lanceur glisse avec elle (ouverture d'un panneau latéral,
// barre d'actions) au lieu de sauter
:global(body.zendesk-loaded iframe#launcher) {
	z-index: 1 !important;
	right: var(--zendesk-right) !important;
	bottom: var(--zendesk-bottom) !important;
	transform-origin: bottom right !important;
	transform: scale(0.75) !important;
	transition: right 0.3s ease-out, bottom 0.3s ease-out !important;
}

// Fenêtre de chat ouverte (classe posée par ZendeskService) : le lanceur, qui sert alors à la
// fermer, passe au-dessus du panneau de discussion
:global(body.zendesk-open iframe#launcher) {
	z-index: 3 !important;
}

// Comme le lanceur : sous le panneau de discussion, et même fondu à l'apparition que celui que
// Zendesk joue sur le lanceur (rejoué à la réapparition, l'animation étant retirée au masquage)
:global(body.zendesk-loaded div:has(~ iframe#launcher)) {
	z-index: 1 !important;
	right: var(--zendesk-right) !important;
	bottom: calc(var(--zendesk-bottom) + 3.5rem) !important;
	transition: right 0.3s ease-out, bottom 0.3s ease-out !important;
	animation: zendesk-fade-in 0.2s ease-in 0.5s both;
}

@keyframes zendesk-fade-in {
	from { opacity: 0; }
	to { opacity: 1; }
}

// Fenêtre flottante seulement : sur petit écran (seuil propre à Zendesk, ~560px) elle passe en
// plein écran (style inline « inset: 0px »), on n'y touche pas
:global(body.zendesk-loaded div:has(+ div > iframe#launcher) iframe:not([style*="inset: 0px"])) {
	z-index: 2 !important;
	right: var(--zendesk-right) !important;
	bottom: calc(var(--zendesk-bottom) + 3.5rem) !important;
	max-height: calc(100dvh - var(--zendesk-bottom) - 4.5rem) !important;
}

@media @layoutNavBottom {
	:global(body.zendesk-loaded) {
		--zendesk-bottom: 6rem;
	}

	:global(body.zendesk-loaded.orion-body--footer-fixed-visible iframe#launcher) {
		display: none !important;
	}

	// visibility et non display pour le message : Zendesk mesure son contenu pour dimensionner
	// l'iframe, masqué en display:none il le fige à 0x0 (croix seule à la réapparition)
	:global(body.zendesk-loaded.orion-body--footer-fixed-visible div:has(~ iframe#launcher)) {
		visibility: hidden !important;
		animation: none;
	}
}

@media @phone {
	:global(body.zendesk-loaded) {
		--zendesk-right: 5rem;
		--zendesk-bottom: 4.5rem;
	}

	:global(body.zendesk-loaded:has(#app > #right-aside:not(:empty)) iframe#launcher) {
		display: none !important;
	}

	:global(body.zendesk-loaded:has(#app > #right-aside:not(:empty)) div:has(~ iframe#launcher)) {
		visibility: hidden !important;
		animation: none;
	}
}
</style>
